import React, { useMemo, useCallback } from 'react';
import { encode } from 'html-entities';
import '@wix/ricos/css/ricos-viewer.global.css';
import '@wix/ricos/css/all-plugins-viewer.css';
import '@wix/ricos/css/plugin-audio-viewer.global.css';
import '@wix/ricos/css/plugin-button-viewer.global.css';
import '@wix/ricos/css/plugin-collapsible-list-viewer.global.css';
import '@wix/ricos/css/plugin-divider-viewer.global.css';
import '@wix/ricos/css/plugin-file-upload-viewer.global.css';
import '@wix/ricos/css/plugin-gallery-viewer.global.css';
import '@wix/ricos/css/plugin-giphy-viewer.global.css';
import '@wix/ricos/css/plugin-hashtag-viewer.global.css';
import '@wix/ricos/css/plugin-html-viewer.global.css';
import '@wix/ricos/css/plugin-image-viewer.global.css';
import '@wix/ricos/css/plugin-link-viewer.global.css';
import '@wix/ricos/css/plugin-link-preview-viewer.global.css';
import '@wix/ricos/css/plugin-mentions-viewer.global.css';
import '@wix/ricos/css/plugin-social-polls-viewer.global.css';
import '@wix/ricos/css/plugin-spoiler-viewer.global.css';
import '@wix/ricos/css/plugin-table-viewer.global.css';
import '@wix/ricos/css/plugin-vertical-embed-viewer.global.css';
import '@wix/ricos/css/plugin-video-viewer.global.css';

import {
  RicosViewer,
  ensureRicosContent,
  ensureDraftContent,
  fromDraft,
  toDraft,
  pluginCodeBlockViewer,
  pluginHashtagViewer,
  pluginIndentViewer,
  pluginLineSpacingViewer,
  pluginSpoilerViewer,
  pluginTextColorViewer,
  pluginTextHighlightViewer,
  pluginEmojiViewer,
} from '@wix/ricos';
import {
  pluginActionButtonLoadable,
  pluginAudioLoadable,
  pluginCollapsibleListLoadable,
  pluginDividerLoadable,
  pluginFileUploadLoadable,
  pluginGalleryLoadable,
  pluginGiphyLoadable,
  pluginHtmlLoadable,
  pluginImageLoadable,
  pluginLinkButtonLoadable,
  pluginLinkLoadable,
  pluginLinkPreviewLoadable,
  pluginMentionsLoadable,
  pluginPollLoadable,
  pluginTableLoadable,
  pluginVerticalEmbedLoadable,
  pluginVideoLoadable,
} from '@wix/ricos/loadable';

import { DraftJSObject } from '../../../../types/RichContent';
import { st, classes } from '../QuestionsWrapper/styles.st.css';
import { useRCVInstance, useRCVSettings } from './RCVSettingsContext';
import { getDomain, getMediaFileType } from './getMediaFileType';

enum PlatformType {
  Livesite = 'Livesite',
  Editor = 'Editor',
}
interface AudioSrc {
  id: string;
}
const plugins = [
  pluginActionButtonLoadable(),
  pluginAudioLoadable({
    getAudioUrl: (src: AudioSrc) => `https://static.wixstatic.com/${src.id}`,
  }),
  pluginCodeBlockViewer(),
  pluginCollapsibleListLoadable(),
  pluginDividerLoadable(),
  pluginEmojiViewer(),
  pluginGiphyLoadable(),
  pluginHashtagViewer(),
  pluginHtmlLoadable({
    width: '100%',
    minWidth: '100%',
    siteDomain: 'https://nivina3.wixsite.com/my-site-334',
  }),
  pluginImageLoadable({ disableTransition: true }),
  pluginIndentViewer(),
  pluginLineSpacingViewer(),
  pluginLinkButtonLoadable(),
  pluginLinkPreviewLoadable(),
  pluginLinkLoadable(),
  pluginMentionsLoadable(),
  pluginPollLoadable(),
  pluginSpoilerViewer(),
  pluginTableLoadable(),
  pluginTextColorViewer(),
  pluginTextHighlightViewer(),
  pluginVerticalEmbedLoadable(),
  pluginVideoLoadable(),
];
const idContainsPrefix = (id: string) => {
  return id.includes('/');
};
interface ResolveFileUrlProps {
  id: string;
  type: string;
  privacy: string;
  url: string;
  name: string;
}

const resolveFileUrl =
  (metaSiteId: string) =>
  ({
    id,
    type,
    privacy,
    url,
    name,
  }: {
    id: string;
    type: string;
    privacy: string;
    url: string;
    name: string;
  }) => {
    if (privacy !== 'private' && idContainsPrefix(id) && metaSiteId) {
      try {
        const domain = getDomain(getMediaFileType(type), metaSiteId);
        return Promise.resolve(
          `https://${domain}/${id}?dn=${encodeURIComponent(name)}`,
        );
      } catch (error) {
        // Fall back to original URL if domain resolution fails
        return Promise.resolve(url);
      }
    }
    return Promise.resolve(url);
  };

interface RCVProps {
  convertAnswerToJSON: DraftJSObject | string;
  answerQuestionId: string;
}

const minimalDraftContent: DraftJSObject = {
  blocks: [
    {
      key: '',
      text: '',
      type: 'unstyled',
      depth: 0,
      inlineStyleRanges: [],
      entityRanges: [],
      data: {},
    },
  ],
  entityMap: {},
  documentStyle: {},
  VERSION: '1.0.0',
  ID: '',
};

const RCVComponent = ({ convertAnswerToJSON, answerQuestionId }: RCVProps) => {
  const { metaSiteId, instance } = useRCVInstance();
  const {
    layout,
    textAlignment,
    isMobile,
    isEditor,
    customStyles: customStylesFromContext,
    palette,
  } = useRCVSettings();
  const validContent = useMemo(
    () =>
      typeof convertAnswerToJSON === 'string'
        ? encode(convertAnswerToJSON)
        : toDraft(
            fromDraft(ensureDraftContent(convertAnswerToJSON), {
              ignoreUnsupportedValues: true,
            }),
          ),
    [convertAnswerToJSON],
  );
  const direction = textAlignment === 'right' ? 'rtl' : 'ltr';
  const parentClass = st(
    classes.answerWrapper,
    { layout },
    answerQuestionId ? `RCV${answerQuestionId}` : undefined,
  );

  const content = useMemo(
    () =>
      ensureRicosContent(
        typeof validContent === 'string'
          ? minimalDraftContent
          : (validContent as DraftJSObject),
      ),
    [validContent],
  );

  const fileUploadResolver = useCallback(
    (src: ResolveFileUrlProps) => {
      if (!metaSiteId) {
        return Promise.resolve(src.url);
      }
      return resolveFileUrl(metaSiteId)(src);
    },
    [metaSiteId],
  );

  const viewerPlugins = useMemo(
    () => [
      pluginFileUploadLoadable({
        resolveFileUrl: fileUploadResolver,
        downloadTarget: '_blank',
      }),
      pluginGalleryLoadable({
        scopingId: answerQuestionId,
      }),
      ...plugins,
    ],
    [fileUploadResolver, answerQuestionId],
  );

  const biSettings = useMemo(
    () => ({
      consumer: '[wix-labs] faq-ooi',
      usage: 'answer',
      platform: isEditor ? PlatformType.Editor : PlatformType.Livesite,
      postId: answerQuestionId,
      containerId: metaSiteId || answerQuestionId || 'faq-ooi-answer',
    }),
    [isEditor, answerQuestionId, metaSiteId],
  );

  const theme = useMemo(
    () => ({
      parentClass,
      customStyles: customStylesFromContext ?? {},
      palette: palette ?? {},
    }),
    [parentClass, customStylesFromContext, palette],
  );

  return (
    <div
      style={{ direction }}
      className={st(classes.answer, { layout, mobile: isMobile })}
    >
      {typeof convertAnswerToJSON === 'string' ? (
        convertAnswerToJSON
      ) : (
        <RicosViewer
          isMobile={isMobile}
          biSettings={biSettings}
          instance={instance}
          theme={theme}
          textAlignment={textAlignment}
          plugins={viewerPlugins}
          content={content}
        />
      )}
    </div>
  );
};
const RCV = React.memo(RCVComponent);
RCV.displayName = 'FaqOoiRCVAnswer';
export default RCV;
